import { useState, useEffect, useRef } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Loader2, Search, X } from 'lucide-react'

import { AdminLayout } from '../../../layouts/admin-layout'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'

interface Message {
  id: number
  content: string | null
  cost_usd: number | null
  model: string | null
  created_at: string
  user_email: string | null
  user_name: string | null
  project_name: string | null
  project_id: number | null
  organization_name: string | null
  organization_id: number | null
  conversation_id: number | null
  conversation_title: string | null
}

interface Filters {
  project_id: string | null
  project_name: string | null
  user_email: string | null
  organization_id: string | null
  organization_name: string | null
  search: string | null
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  messages: Message[]
  pagy: Pagination
  filters: Filters
}

interface ProjectResult {
  id: number
  name: string
  organization_name: string | null
}

interface OrgResult {
  id: number
  name: string
  member_count: number
}

function SearchPopover<T extends { id: number; name: string }>({
  selected,
  onSelect,
  onClear,
  placeholder,
  searchUrl,
  renderResult,
}: {
  selected: { id: string; name: string } | null
  onSelect: (result: T) => void
  onClear: () => void
  placeholder: string
  searchUrl: string
  renderResult: (result: T) => React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 2) {
      setResults([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' },
        })
        if (response.ok) {
          const data = await response.json()
          setResults(data)
        }
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, open, searchUrl])

  const handleSelect = (result: T) => {
    onSelect(result)
    setOpen(false)
    setQuery('')
    setResults([])
  }

  if (selected) {
    return (
      <div className="flex items-center gap-1 h-7 px-2 text-xs border rounded-md bg-muted">
        <span className="truncate max-w-[120px]">{selected.name}</span>
        <button onClick={onClear} className="shrink-0 hover:text-foreground text-muted-foreground">
          <X className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-7 px-2 text-xs gap-1">
          <Search className="h-3 w-3" />
          {placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isSearching && query.length >= 2 && results.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No results</p>
          )}
          {!isSearching && query.length < 2 && (
            <p className="py-4 text-center text-sm text-muted-foreground">Type to search...</p>
          )}
          {results.map((result) => (
            <button
              key={result.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent cursor-pointer"
              onClick={() => handleSelect(result)}
            >
              {renderResult(result)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function formatModelLabel(model: string | null): string {
  if (!model) return '-'
  const claudeMatch = model.match(/claude-(\w+-[\d.-]+)/)
  if (claudeMatch) return claudeMatch[1].replace(/-\d{8}.*$/, '')
  return model
}

function Index({ messages, pagy, filters }: Props) {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const [search, setSearch] = useState(filters.search || '')
  const [userEmail, setUserEmail] = useState(filters.user_email || '')
  const [selectedProject, setSelectedProject] = useState<{ id: string; name: string } | null>(
    filters.project_id ? { id: filters.project_id, name: filters.project_name || `Project #${filters.project_id}` } : null
  )
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string } | null>(
    filters.organization_id ? { id: filters.organization_id, name: filters.organization_name || `Org #${filters.organization_id}` } : null
  )

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}
    if (search) params['search'] = search
    if (userEmail) params['user_email'] = userEmail
    if (selectedProject) params['project_id'] = selectedProject.id
    if (selectedOrg) params['organization_id'] = selectedOrg.id
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleSearch = () => {
    router.get('/admin/messages', buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearch('')
    setUserEmail('')
    setSelectedProject(null)
    setSelectedOrg(null)
    router.get('/admin/messages', {}, { preserveState: true })
  }

  const handleProjectSelect = (result: ProjectResult) => {
    setSelectedProject({ id: String(result.id), name: result.name })
    // Trigger search immediately
    const params: Record<string, string> = {}
    if (search) params['search'] = search
    if (userEmail) params['user_email'] = userEmail
    params['project_id'] = String(result.id)
    if (selectedOrg) params['organization_id'] = selectedOrg.id
    router.get('/admin/messages', params, { preserveState: true })
  }

  const handleOrgSelect = (result: OrgResult) => {
    setSelectedOrg({ id: String(result.id), name: result.name })
    const params: Record<string, string> = {}
    if (search) params['search'] = search
    if (userEmail) params['user_email'] = userEmail
    if (selectedProject) params['project_id'] = selectedProject.id
    params['organization_id'] = String(result.id)
    router.get('/admin/messages', params, { preserveState: true })
  }

  const handleProjectClear = () => {
    setSelectedProject(null)
    const params: Record<string, string> = {}
    if (search) params['search'] = search
    if (userEmail) params['user_email'] = userEmail
    if (selectedOrg) params['organization_id'] = selectedOrg.id
    router.get('/admin/messages', params, { preserveState: true })
  }

  const handleOrgClear = () => {
    setSelectedOrg(null)
    const params: Record<string, string> = {}
    if (search) params['search'] = search
    if (userEmail) params['user_email'] = userEmail
    if (selectedProject) params['project_id'] = selectedProject.id
    router.get('/admin/messages', params, { preserveState: true })
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 60) return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('messages.index.titleWithCount', { count: pagy.count })}
              </span>
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder={t('messages.index.filters.searchPlaceholder')}
                className="h-7 text-xs w-40"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Input
                type="text"
                placeholder={t('messages.index.filters.userPlaceholder')}
                className="h-7 text-xs w-36"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <SearchPopover<ProjectResult>
                selected={selectedProject}
                onSelect={handleProjectSelect}
                onClear={handleProjectClear}
                placeholder="Project..."
                searchUrl="/admin/projects/search"
                renderResult={(r) => (
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="truncate w-full">{r.name}</span>
                    {r.organization_name && (
                      <span className="text-xs text-muted-foreground truncate w-full">{r.organization_name}</span>
                    )}
                  </div>
                )}
              />
              <SearchPopover<OrgResult>
                selected={selectedOrg}
                onSelect={handleOrgSelect}
                onClear={handleOrgClear}
                placeholder="Organization..."
                searchUrl="/admin/organizations/search"
                renderResult={(r) => (
                  <div className="flex flex-col items-start min-w-0 flex-1">
                    <span className="truncate w-full">{r.name}</span>
                    <span className="text-xs text-muted-foreground">{r.member_count} members</span>
                  </div>
                )}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                {tc('buttons.search')}
              </Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                {tc('buttons.reset')}
              </Button>
            </div>

            {/* Messages Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground w-[40%]">
                        {t('messages.index.table.message')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('messages.index.table.user')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('messages.index.table.project')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('messages.index.table.organization')}
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        Cost
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('messages.index.table.model')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('messages.index.table.time')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {messages.length > 0 ? (
                      messages.map((msg) => (
                        <tr key={msg.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <p className="text-sm text-foreground line-clamp-2">
                              {msg.content || <span className="text-muted-foreground italic">Empty</span>}
                            </p>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <div>
                              {msg.user_name && (
                                <p className="text-sm font-medium text-foreground">{msg.user_name}</p>
                              )}
                              <p className="text-xs text-muted-foreground">{msg.user_email}</p>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {msg.project_id ? (
                              <Link
                                href={`/admin/projects/${msg.project_id}`}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                {msg.project_name}
                              </Link>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {msg.organization_id ? (
                              <Link
                                href={`/admin/organizations/${msg.organization_id}`}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                {msg.organization_name}
                              </Link>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            {msg.cost_usd && msg.cost_usd > 0 ? (
                              <span className="text-sm text-foreground">${msg.cost_usd.toFixed(4)}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-xs text-muted-foreground font-mono">
                              {formatModelLabel(msg.model)}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {formatRelativeTime(msg.created_at)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('messages.index.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get('/admin/messages', { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        {t('messages.index.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get('/admin/messages', { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        {t('messages.index.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
